'use client';

import React, { useCallback } from 'react';
import { Box, Typography } from '@mui/material';

interface CompactCalendarDayProps {
    date: Date;
    isSelected: boolean;
    isDisabled: boolean;
    isCurrentMonth: boolean;
    onClick: () => void;
    isHomepage?: boolean;
}

const CompactCalendarDay = ({
    date,
    isSelected,
    isDisabled,
    isCurrentMonth,
    onClick,
    isHomepage = false
}: CompactCalendarDayProps) => {
    // Improved handler that stops event propagation
    const handleClick = useCallback((e: React.MouseEvent) => {
        if (!isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        }
    }, [isDisabled, onClick]);

    // Handle touch events separately for mobile
    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (!isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        }
    }, [isDisabled, onClick]);

    // Today's date for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateClone = new Date(date);
    dateClone.setHours(0, 0, 0, 0);
    const isToday = dateClone.getTime() === today.getTime();

    return (
        <Box
            onClick={handleClick}
            onTouchEnd={handleTouchEnd}
            sx={{
                width: '100%',
                aspectRatio: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDisabled ? 'default' : 'pointer',
                borderRadius: '50%',
                backgroundColor: isSelected ? '#C0C0DD' : 'transparent',
                border: isToday && !isSelected ? '1px solid rgba(224, 177, 241, 0.8)' : 'none',
                transition: 'transform 0.1s ease-in-out, background-color 0.1s ease-in-out',
                '&:hover': {
                    backgroundColor: !isDisabled && !isSelected ? 'rgba(224, 177, 241, 0.15)' : undefined,
                    transform: !isDisabled ? 'scale(1.1)' : undefined,
                },
                opacity: isDisabled ? 0.4 : 1,
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    fontWeight: isSelected || isToday ? 700 : 400,
                    color: isSelected ? '#1A1B23' : '#FFFFFF',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    fontFamily: 'Inter',
                    userSelect: 'none',
                }}
            >
                {date.getDate()}
            </Typography>
        </Box>
    );
};

export default React.memo(CompactCalendarDay); 